import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, AlertCircle, Lock } from 'lucide-react';

const TwoFactorLoginVerification = ({ email, tempToken, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🔐 Verifying 2FA with tempToken:', tempToken);
    console.log('🔐 Code:', code);

    try {
      const response = await fetch(`${API}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({ token: code })
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        onSuccess(data.access_token, data.user, data.refresh_token);
      } else {
        setError(data.detail || 'Invalid verification code');
      }
    } catch (err) {
      setError('Error verifying code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-emerald-100 p-3 rounded-lg">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <CardTitle>Two-Factor Authentication</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900 font-medium">Logging in as:</p>
                <p className="text-sm text-blue-800">{email}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <Input
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code.
              </p>
            </div>

            <div className="flex gap-3">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
            </div>
          </form>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Lost access to your authenticator?</strong> Contact your administrator for assistance with account recovery.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorLoginVerification;
